import { Menu } from '#app/components/Menu/Menu.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { springAnimation } from '#app/utilities/animation.js';
import { Avatar } from '../Avatar/Avatar.js';
import { type AvatarWithPopoverProps } from '../Avatar/types.js';
import { AvatarCard } from '../AvatarCard/AvatarCard.js';
import { AvatarCardWrapper } from '../AvatarCard/AvatarCardWrapper.js';
import { AvatarMask } from '../AvatarMask/AvatarMask.js';
import { AnimatePresence } from 'framer-motion';
import { Suspense, useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const AvatarContainer = styled.div<{ isLinkDisabled: boolean }>`
  ${({ isLinkDisabled }) => css`
    cursor: ${isLinkDisabled ? 'default' : 'pointer'};
    position: relative;
  `}
`;

const GhostCard = styled.div`
  width: 388px;
  height: 302px;
`;

const StyledMenuPanel = styled(Menu.Panel)`
  opacity: 0;
  background-color: transparent;
  overflow: visible;
  justify-content: center;
  align-items: center;
  box-shadow: unset;
  display: flex;
  width: auto;
`;

export const AvatarWithPopover = ({
  avatarImage,
  avatarRef,
  disableAvatarCardLinks = false,
  displayUsername,
  isMasked = false,
  name,
  onClick,
  size,
  strategy = 'fixed',
  ...props
}: AvatarWithPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleOnMouseEnter = useCallback(() => {
    if (!isOpen) setIsOpen(true);
  }, [isOpen, setIsOpen]);

  const handleOnMouseLeave = useCallback(() => {
    if (isOpen) setIsOpen(false);
  }, [isOpen, setIsOpen]);

  return (
    <Menu
      isOpen={isOpen && !isMobile}
      placement="bottom"
      strategy={strategy}
    >
      <div
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      >
        <Menu.Reference>
          <AvatarContainer isLinkDisabled={disableAvatarCardLinks}>
            <Avatar
              avatarImage={avatarImage}
              avatarRef={avatarRef}
              onClick={onClick}
              size={size}
              {...props}
            />
            {isMasked && <AvatarMask size={size} />}
          </AvatarContainer>
        </Menu.Reference>
        {disableAvatarCardLinks ? (
          <StyledMenuPanel>
            <AnimatePresence>
              {displayUsername ? (
                <Suspense
                  fallback={<GhostCard onMouseEnter={handleOnMouseLeave} />}
                >
                  <animated.div
                    animate={{ opacity: 1 }}
                    initial={{ opacity: 0 }}
                    transition={springAnimation.fast}
                  >
                    <AvatarCardWrapper displayUsername={displayUsername} />
                  </animated.div>
                </Suspense>
              ) : name ? (
                <AvatarCard userName={name} />
              ) : null}
            </AnimatePresence>
          </StyledMenuPanel>
        ) : null}
      </div>
    </Menu>
  );
};
